import { Link } from "react-router-dom";
import { ArrowRight, ArrowUpRight, Check, AlertTriangle } from "lucide-react";
import { PRODUCTS, CATEGORIES, INDUSTRIES } from "../lib/data";
import { PageHero, CTABand, FaqSection } from "../components/site/Bits";
import { Reveal } from "../components/site/Reveal";
import CostBenefitChart from "../components/site/CostBenefitChart";
import ProductResources from "../components/site/ProductResources";

import { Seo } from "../components/site/Seo";

export default function CategoryPage({ slug }) {
  const category = CATEGORIES.find((c) => c.slug === slug);
  const products = PRODUCTS.filter((p) => p.categorySlug === slug);

  if (!category) {
    return (
      <div className="pt-[120px] container-x py-24 text-center">
        <h1 className="font-display font-bold text-3xl text-ink">Category not found</h1>
        <Link to="/" className="mt-4 inline-block text-flame font-semibold">Back home</Link>
      </div>
    );
  }

  const lead = products[0];

  const canonical = `https://oneharvestco.com${category.to}`;
  const breadcrumbLd = {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    itemListElement: [
      { "@type": "ListItem", position: 1, name: "Home", item: "https://oneharvestco.com/" },
      { "@type": "ListItem", position: 2, name: category.name, item: canonical },
    ],
  };
  const faqLd = category.faqs?.length
    ? {
        "@context": "https://schema.org",
        "@type": "FAQPage",
        mainEntity: category.faqs.map((f) => ({
          "@type": "Question",
          name: f.q,
          acceptedAnswer: { "@type": "Answer", text: f.a },
        })),
      }
    : null;
  const jsonLd = faqLd ? [breadcrumbLd, faqLd] : [breadcrumbLd];

  return (
    <div data-testid={`category-page-${slug}`}>
      <Seo title={category.name} description={category.desc} image={category.image} jsonLd={jsonLd} />
      <PageHero
        kicker={category.name}
        title={category.tagline}
        subtitle={category.desc}
        image={category.image}
        logo={category.heroLogo}
        testid="category-hero"
      />

      {products.map((product, idx) => (
        <ProductBlock key={product.slug} product={product} index={idx} />
      ))}

      {/* Stewardship callout */}
      <section className="bg-background border-t border-border" data-testid="category-stewardship-callout">
        <div className="container-x py-16">
          <Reveal>
            <div className="border border-border bg-card p-8 md:p-10 flex flex-col md:flex-row md:items-center gap-6 justify-between">
              <div className="max-w-2xl">
                <div className="kicker text-flame mb-3">More Than Products · A Preservation Solution</div>
                <h3 className="font-display font-bold text-2xl md:text-3xl text-ink tracking-tight">
                  {lead ? `${lead.name} also protects the land as a long-cycle asset.` : "We protect the land as a long-cycle asset."}
                </h3>
                <p className="mt-3 text-muted-foreground leading-relaxed">
                  Our solutions keep soil, water, and topsoil clean and intact — preserving value for future agriculture
                  and livestock, and giving operators visionary stewardship leadership.
                </p>
              </div>
              <Link to="/land-stewardship" data-testid="category-stewardship-cta" className="inline-flex items-center justify-center gap-2 bg-flame text-white px-6 py-4 font-semibold hover:bg-flame/90 transition-colors whitespace-nowrap flex-shrink-0">
                See our stewardship approach <ArrowRight className="h-4 w-4" />
              </Link>
            </div>
          </Reveal>
        </div>
      </section>

      <FaqSection faqs={category.faqs} />

      <CTABand />
    </div>
  );
}

function ProductBlock({ product, index }) {
  return (
    <div data-testid={`product-block-${product.slug}`}>
      {/* Overview + advantages */}
      <section className="bg-background">
        <div className="container-x py-20 md:py-24">
          <div className="grid gap-12 lg:grid-cols-12 items-center">
            <Reveal className="lg:col-span-5 order-1 lg:order-none">
              <div className="relative">
                <div className="absolute -inset-3 bg-flame/10" />
                <img src={product.image} alt={product.name}
                  className={`relative w-full border border-border ${product.fit === "contain" ? "object-contain bg-white max-h-[440px] p-4" : "object-cover"}`} />
              </div>
            </Reveal>
            <div className="lg:col-span-7">
              <div className="kicker text-flame">{product.category}</div>
              <h2 className="mt-2 font-display font-black text-ink text-3xl md:text-5xl tracking-tight">{product.name}</h2>
              <p className="mt-4 text-charcoal text-lg leading-relaxed">{product.tagline}</p>
              <p className="mt-4 text-muted-foreground leading-relaxed">{product.summary}</p>
              <div className="mt-7 flex flex-wrap gap-3">
                <Link to="/request-quote" data-testid={`category-quote-${product.slug}`} className="inline-flex items-center gap-2 bg-flame text-white px-6 py-3.5 font-semibold hover:bg-flame/90 transition-colors">
                  Request a Quote <ArrowUpRight className="h-4 w-4" />
                </Link>
                <Link to={`/products/${product.slug}`} data-testid={`category-detail-${product.slug}`} className="inline-flex items-center gap-2 border border-ink text-ink px-6 py-3.5 font-semibold hover:bg-ink hover:text-white transition-colors">
                  Full product details <ArrowRight className="h-4 w-4" />
                </Link>
              </div>
            </div>
          </div>

          <div className="mt-16 grid gap-14 lg:grid-cols-12">
            <div className="lg:col-span-7">
              <h3 className="font-display font-bold text-2xl text-ink">Use cases</h3>
              <div className="mt-4 flex flex-wrap gap-2">
                {product.useCases.map((u) => (
                  <span key={u} className="text-sm bg-muted text-charcoal px-3 py-1.5 border border-border">{u}</span>
                ))}
              </div>

              <h3 className="mt-10 font-display font-bold text-2xl text-ink">Key advantages</h3>
              <div className="mt-6 grid sm:grid-cols-2 gap-4">
                {product.highlights.map((h) => (
                  <div key={h} className="flex items-start gap-3 border border-border bg-card p-5">
                    <Check className="h-5 w-5 text-flame flex-shrink-0 mt-0.5" />
                    <span className="text-charcoal">{h}</span>
                  </div>
                ))}
              </div>
            </div>
            <div className="lg:col-span-5">
              <div className="border border-border bg-card p-8">
                <h3 className="font-display font-bold text-xl text-ink">Common applications</h3>
                <ul className="mt-5 space-y-3">
                  {product.applications.map((a) => (
                    <li key={a} className="flex items-center gap-3 text-charcoal">
                      <span className="h-1.5 w-1.5 bg-flame" /> {a}
                    </li>
                  ))}
                </ul>
                <div className="mt-8 pt-6 border-t border-border">
                  <p className="kicker text-muted-foreground mb-3">Sectors served</p>
                  <div className="flex flex-wrap gap-2">
                    {product.sectors.map((s) => {
                      const sec = INDUSTRIES.find((i) => i.slug === s);
                      if (!sec) return null;
                      return (
                        <Link key={s} to={`/industries/${s}`} className="text-xs border border-border px-2.5 py-1 text-charcoal hover:border-flame hover:text-flame transition-colors">
                          {sec.name}
                        </Link>
                      );
                    })}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {product.packaging && product.packaging.length > 0 && (
        <section data-testid={`packaging-${product.slug}`} className="bg-ink relative overflow-hidden">
          <div className="absolute inset-0 grain opacity-10" />
          <div className="container-x py-20 md:py-24 relative">
            <div className="max-w-3xl">
              <div className="kicker text-flame mb-3">{product.name} · Available Packaging</div>
              <h2 className="font-display font-black text-white text-3xl md:text-4xl tracking-tight text-balance">
                One absorbent. Sized for every operation.
              </h2>
              <p className="mt-4 text-white/70 text-lg leading-relaxed">
                From grab-and-go spill kits to bulk supply for refineries and ports — choose the pack that fits your job.
              </p>
            </div>

            <div className="mt-12 grid gap-6 md:grid-cols-3">
              {product.packaging.map((pk, i) => (
                <Reveal key={pk.name} delay={i * 0.08}>
                  <div data-testid={`packaging-item-${i}`} className="group relative h-full flex flex-col border border-white/10 bg-white/[0.03] overflow-hidden hover:border-flame/60 hover:-translate-y-1.5 hover:shadow-2xl transition-[transform,box-shadow,border-color] duration-300 ease-[cubic-bezier(0.25,1,0.5,1)]">
                    <span aria-hidden="true" className="absolute top-0 left-0 z-20 h-full w-1 bg-flame origin-top scale-y-0 group-hover:scale-y-100 transition-transform duration-500 ease-out" />
                    <div className="relative aspect-square bg-white flex items-center justify-center p-6">
                      <img src={pk.image} alt={`${product.name} ${pk.name}`} className="max-h-full max-w-full object-contain transition-transform duration-700 ease-[cubic-bezier(0.25,1,0.5,1)] group-hover:scale-105" />
                    </div>
                    <div className="p-6 border-t border-white/10 flex flex-col flex-1">
                      <h3 className="font-display font-bold text-xl text-white">{pk.name}</h3>
                      <p className="mt-1 text-sm text-flame font-semibold">{pk.tagline}</p>
                      <ul className="mt-4 space-y-2.5 flex-1">
                        {pk.points.map((pt) => (
                          <li key={pt} className="flex items-start gap-2.5 text-sm text-white/70 leading-relaxed">
                            <Check className="h-4 w-4 text-flame shrink-0 mt-0.5" /> {pt}
                          </li>
                        ))}
                      </ul>
                    </div>
                  </div>
                </Reveal>
              ))}
            </div>

            <div className="mt-10">
              <Link to="/request-quote?source=mat-catalog&mat=BoneDry" data-testid={`packaging-quote-${product.slug}`} className="inline-flex items-center gap-2 bg-flame text-white px-7 py-4 font-semibold hover:bg-flame/90 transition-colors">
                Request {product.name} pricing <ArrowUpRight className="h-4 w-4" />
              </Link>
            </div>
          </div>
        </section>
      )}

      {product.costBenefit && (
        <section data-testid={`cost-benefit-${product.slug}`} className="bg-muted">
          <div className="container-x py-20">
            <Reveal>
              <CostBenefitChart data={product.costBenefit} />
            </Reveal>
          </div>
        </section>
      )}

      {product.premierLine && (
        <section data-testid={`premier-line-${product.slug}`} className="bg-background">
          <div className="container-x py-24">
            <div className="max-w-3xl">
              <div className="kicker text-flame mb-3">{product.premierLine.kicker || "Product Line"}</div>
              <h2 className="font-display font-extrabold text-3xl md:text-4xl text-ink tracking-tight">{product.premierLine.title}</h2>
              <p className="mt-4 text-muted-foreground text-lg leading-relaxed">{product.premierLine.intro}</p>
            </div>

            <div className="mt-10 kicker text-charcoal">{product.premierLine.forSaleLabel || `The ${product.name} line`}</div>

            <div className="mt-4 hidden lg:block border border-border bg-card">
              <div className="grid grid-cols-12 bg-ink text-white text-xs uppercase tracking-widest">
                <div className="col-span-3 p-4 border-r border-white/10">Product</div>
                <div className="col-span-4 p-4 border-r border-white/10">Best-fit use case</div>
                <div className="col-span-5 p-4">When to recommend</div>
              </div>
              {product.premierLine.products.map((row, i) => (
                <div key={row.name} className={`grid grid-cols-12 ${i % 2 ? "bg-muted/40" : "bg-card"} border-t border-border`}>
                  <div className="col-span-3 p-4 border-r border-border"><span className="font-display font-bold text-ink">{row.name}</span></div>
                  <div className="col-span-4 p-4 border-r border-border text-sm text-charcoal leading-relaxed">{row.bestFit}</div>
                  <div className="col-span-5 p-4 text-sm text-muted-foreground leading-relaxed">{row.react}</div>
                </div>
              ))}
            </div>

            <div className="mt-4 grid gap-4 lg:hidden">
              {product.premierLine.products.map((row) => (
                <div key={row.name} className="border border-border bg-card p-6">
                  <h3 className="font-display font-bold text-xl text-ink">{row.name}</h3>
                  <div className="mt-3"><div className="kicker text-flame">Best-fit use case</div><p className="mt-1 text-sm text-charcoal leading-relaxed">{row.bestFit}</p></div>
                  <div className="mt-3"><div className="kicker text-muted-foreground">When to recommend</div><p className="mt-1 text-sm text-muted-foreground leading-relaxed">{row.react}</p></div>
                </div>
              ))}
            </div>

            <div className="mt-10 grid gap-6 md:grid-cols-2">
              <div className="border-l-2 border-flame bg-card p-6">
                <div className="kicker text-flame mb-2">Packaging</div>
                <p className="text-sm text-charcoal leading-relaxed">{product.premierLine.packaging}</p>
              </div>
              <div className="border-l-2 border-ink bg-card p-6">
                <div className="kicker text-ink mb-2">How we recommend</div>
                <p className="text-sm text-charcoal leading-relaxed">{product.premierLine.closing}</p>
              </div>
            </div>

            <Link to="/request-quote" data-testid={`premier-quote-${product.slug}`} className="mt-8 inline-flex items-center gap-2 bg-flame text-white px-6 py-4 font-semibold hover:bg-flame/90 transition-colors">
              Get a tailored recommendation <ArrowUpRight className="h-4 w-4" />
            </Link>

            {product.premierLine.disposal && (
              <div className="mt-10 flex items-start gap-4 border border-flame/40 bg-flame/5 p-6">
                <AlertTriangle className="h-6 w-6 text-flame flex-shrink-0 mt-0.5" />
                <div>
                  <div className="kicker text-flame mb-1">Disposal & Compliance Notice</div>
                  <p className="text-sm text-charcoal leading-relaxed">{product.premierLine.disposal}</p>
                </div>
              </div>
            )}
          </div>
        </section>
      )}

      <ProductResources slug={product.slug} productName={product.name} />
    </div>
  );
}
